// Team records for the TRF export.
//
// Split out of js/services/trf-export.js rather than folded into it: every record here is
// emitted ONLY for a team tournament, and keeping them together means the individual
// export stays exactly as long and as readable as it was. The fixed-width layout comes
// from TRFExport.buildLine, so there is still one implementation of TRF column placement.
//
// SOURCES, and why both generations of record are written.
//
//   TRF16 (FIDE Handbook C.04 Annex 2, approved Elista 2015) defines the team tournament
//   with record 013 plus 082. That is what every FIDE rating submission accepts today.
//
//   TRF25 (FIDE Technical Commission final draft, January 2025) replaces 013 with 310,
//   which carries the same registration order plus the team's pairing number, match
//   points, game points and rank — things a reader previously had to recompute. TRF25 is
//   explicit that 013 "could not be effectively extended", and that a program seeking FIDE
//   approval for team tournaments must produce BOTH records on a transitional basis. So
//   this module writes 013 in every version and adds 310 alongside it in the newer one,
//   which is what the transition asks for, not a duplication bug.
//
// Column positions below are quoted from those two documents and are not negotiable —
// a TRF reader indexes by column, so an off-by-one silently corrupts a team's line-up
// rather than failing loudly.

const TRFTeam = {

    /**
     * Record 082 — number of teams. TRF16 tournament section, "in case of a team
     * tournament", which is why the individual export omits it entirely.
     */
    teamCountLine(teams) {
        return '082 ' + (teams || []).length;
    },

    /**
     * Record 013 — the team section.
     *
     *   1-3    '013'
     *   5-36   team name (32)
     *   37-40  1st player's starting rank number, then every 5 columns:
     *          42-45, 47-50, 52-55 … (8th at 72-75, 14th at 102-105)
     *
     * The order IS the meaning: TRF25 notes that this record "defines the registration
     * order of the team players, and the order in which they have to play when the board
     * order is fixed". So `squad` must be the registered board order, not a re-sort.
     */
    teamLine(team, startNoOf) {
        const fields = [
            { col: 1, text: '013', width: 3 },
            { col: 5, text: team.name || '', width: 32 }
        ];
        (team.squad || []).forEach((playerId, i) => {
            const no = startNoOf(playerId);
            if (!no) return;
            fields.push({ col: 37 + i * 5, text: no, width: 4, align: 'right' });
        });
        return window.TRFExport.buildLine(fields);
    },

    /**
     * Record 310 — the team section that replaces 013.
     *
     *   1-3     '310'
     *   5-7     team pairing number (1-999)
     *   9-40    team name (32)
     *   42-46   team nickname (5) — the handle records 801/802 refer to
     *   48-53   strength factor (6)
     *   55-60   match points (6, format 1111.5)
     *   62-67   game points (6, format 1111.5)
     *   69-71   team rank (ties allowed, so not unique)
     *   74-77   1st player, then every 5 columns: 79-82, 84-87, 89-92 …
     *           (8th at 109-112, 14th at 139-142)
     */
    teamLine310(team, startNoOf) {
        const fields = [
            { col: 1, text: '310', width: 3 },
            { col: 5, text: parseInt(team.teamNo, 10) || 0, width: 3, align: 'right' },
            { col: 9, text: team.name || '', width: 32 },
            { col: 42, text: (team.nickname || '').toUpperCase(), width: 5 },
            // Strength factor is whatever the competition ranks teams by — usually the
            // average rating of the top boards. Left blank when the event never set one,
            // because a fabricated 0 would read as "a team with no strength".
            { col: 48, text: team.strength != null && team.strength !== '' ? String(team.strength) : '', width: 6, align: 'right' },
            { col: 55, text: Number(team.matchPoints || 0).toFixed(1), width: 6, align: 'right' },
            { col: 62, text: Number(team.gamePoints || 0).toFixed(1), width: 6, align: 'right' },
            { col: 69, text: team.rank || '', width: 3, align: 'right' }
        ];
        (team.squad || []).forEach((playerId, i) => {
            const no = startNoOf(playerId);
            if (!no) return;
            fields.push({ col: 74 + i * 5, text: no, width: 4, align: 'right' });
        });
        return window.TRFExport.buildLine(fields);
    },

    /**
     * Record 352 — the colour sequence down the boards, e.g. 'WBWB' for a four-board
     * match. The number of boards is implicit in the string's length, which is why this
     * record is also how a reader learns how wide a match is.
     *
     * The team holding White takes White on the odd boards, so the sequence always starts
     * at 'W' and alternates. Generated rather than stored, so it cannot contradict what
     * TeamEngine.expandMatch() actually produced.
     */
    colourSequenceLine(boards) {
        let seq = '';
        for (let b = 0; b < boards; b++) seq += (b % 2 === 0) ? 'W' : 'B';
        return '352 ' + seq;
    },

    /**
     * Record 362 — the match-point system.
     *
     *   5-6    TW | TD | TL      7-10   its value (format 11.5)
     *   14-15  another of them   16-19  its value
     *   23-24  the last one      25-28  its value
     *
     * Mandatory only when a value differs from the FIDE default (win 2.0, draw 1.0,
     * loss 0.0), so a default event emits nothing and stays byte-identical to what a
     * reader expects. Returns null when there is nothing to declare.
     */
    matchPointSystemLine(matchPointSystem) {
        const [w, d, l] = (matchPointSystem || '2-1-0').split('-').map(Number);
        const win = Number.isFinite(w) ? w : 2;
        const draw = Number.isFinite(d) ? d : 1;
        const loss = Number.isFinite(l) ? l : 0;
        if (win === 2 && draw === 1 && loss === 0) return null;

        const entries = [['TW', win], ['TD', draw], ['TL', loss]];
        const fields = [{ col: 1, text: '362', width: 3 }];
        entries.forEach(([code, value], i) => {
            const base = 5 + i * 9;
            fields.push({ col: base, text: code, width: 2 });
            fields.push({ col: base + 2, text: value.toFixed(1), width: 4, align: 'right' });
        });
        return window.TRFExport.buildLine(fields);
    },

    /**
     * Record 320 — the pairing-allocated bye, one record for the whole tournament.
     *
     *   5-8    match points awarded for a PAB (format 11.5)
     *   10-13  game points awarded for a PAB
     *   15-17  the team that got the PAB in round 1, then every 4 columns:
     *          19-21, 23-25 … (9th round at 47-49). '000' means nobody did.
     *
     * Returns null when no team ever sat out, which is the common even-field case.
     *
     * @param {Array} byeByRound  index 0 = round 1, each entry a TPN or null
     */
    pabLine(byeByRound, pabMatchPoints, pabGamePoints) {
        if (!byeByRound || !byeByRound.some(t => t)) return null;
        const fields = [
            { col: 1, text: '320', width: 3 },
            { col: 5, text: Number(pabMatchPoints || 0).toFixed(1), width: 4, align: 'right' },
            { col: 10, text: Number(pabGamePoints || 0).toFixed(1), width: 4, align: 'right' }
        ];
        byeByRound.forEach((teamNo, i) => {
            fields.push({
                col: 15 + i * 4,
                text: String(teamNo || 0).padStart(3, '0'),
                width: 3,
                align: 'right'
            });
        });
        return window.TRFExport.buildLine(fields);
    },

    /**
     * Record 330 — a match one or both teams did not turn up for.
     *
     *   5-6    '+-' White wins by forfeit | '-+' Black does | '--' neither showed up
     *   8-10   round number
     *   12-14  the team scheduled to play White
     *   16-18  the team scheduled to play Black
     *
     * A team "shows up" if at least one player is present, so this covers only a WHOLE
     * team failing to appear — an individual empty board is an ordinary forfeit in that
     * player's 001 record and a 300 record for the line-up.
     */
    forfeitLine(type, roundNo, whiteTeamNo, blackTeamNo) {
        return window.TRFExport.buildLine([
            { col: 1, text: '330', width: 3 },
            { col: 5, text: type, width: 2 },
            { col: 8, text: String(roundNo).padStart(3, '0'), width: 3, align: 'right' },
            { col: 12, text: String(whiteTeamNo).padStart(3, '0'), width: 3, align: 'right' },
            { col: 16, text: String(blackTeamNo).padStart(3, '0'), width: 3, align: 'right' }
        ]);
    },

    /**
     * Record 300 — a team that played "out of default order" (OOdO).
     *
     *   5-7    round number
     *   9-11   the team playing out of order
     *   13-15  its scheduled opponent
     *   17-20  the player on board 1, then every 5 columns: 22-25, 27-30, 32-35 …
     *          ('0000' for an unoccupied board)
     *
     * TRF25 defines the default order as the registration order in record 310, and says a
     * team playing 1-3-5-6 still FOLLOWS it — holes are fine, reordering is not. A team is
     * also OOdO whenever it leaves a board empty. So this record is emitted exactly when
     * the fielded line-up is not a prefix-preserving subsequence of the squad, or a board
     * is unoccupied; a team that played its top boards in order produces nothing.
     *
     * @returns {string|null} null when the team played in default order with a full board
     */
    outOfOrderLine(roundNo, teamNo, opponentTeamNo, lineup, squad, boards, startNoOf) {
        const ids = [];
        for (let b = 0; b < boards; b++) ids.push(lineup[b] || null);

        const played = ids.filter(id => id);
        const positions = played.map(id => squad.indexOf(id));
        const inOrder = positions.every((p, i) => i === 0 || p > positions[i - 1]);
        const complete = played.length === boards;
        if (inOrder && complete) return null;

        const fields = [
            { col: 1, text: '300', width: 3 },
            { col: 5, text: String(roundNo).padStart(3, '0'), width: 3, align: 'right' },
            { col: 9, text: String(teamNo).padStart(3, '0'), width: 3, align: 'right' },
            { col: 13, text: String(opponentTeamNo).padStart(3, '0'), width: 3, align: 'right' }
        ];
        ids.forEach((id, i) => {
            const no = id ? startNoOf(id) : 0;
            fields.push({
                col: 17 + i * 5,
                text: String(no || 0).padStart(4, '0'),
                width: 4,
                align: 'right'
            });
        });
        return window.TRFExport.buildLine(fields);
    },

    /**
     * Record 192 — the encoded tournament type, from the Tournament Type Code Table
     * (FIDE TEC, January 2025). The team codes are not free-form: they spell out the
     * colour-preference type and the scoring, so the file states the exact rules variant
     * the round was paired under.
     *
     *   FIDE_TEAM_TYPEA_MP_GP   Type A preferences, match points primary, game points
     *                           secondary — this is what plain FIDE_TEAM defaults to
     *   FIDE_TEAM_TYPEB_GP_MP   Type B preferences, game points primary
     *   FIDE_TEAM_MP_GP         no colour preferences at all
     *   BERGER_TEAM_ROUNDROBIN  teams on a Berger table
     *
     * Writing the fully spelled-out code rather than the bare FIDE_TEAM default is
     * deliberate: the default is defined by when the file is read, and a stored file
     * should not change meaning later.
     */
    tournamentTypeCode(settings = {}) {
        const pairing = settings.pairingSystem || 'swiss';
        if (pairing === 'round-robin') return 'BERGER_TEAM_ROUNDROBIN';
        if (pairing === 'double-round-robin') return 'BERGER_TEAM_DOUBLEROUNDROBIN';

        const primary = settings.primaryScore === 'game' ? 'GP' : 'MP';
        const secondary = primary === 'GP' ? 'MP' : 'GP';
        const type = settings.colourPrefType;

        // 'none' is a real, coded option — some competitions ignore colour for teams
        // entirely, and the table gives those events their own codes.
        const typePart = type === 'B' ? 'TYPEB_' : (type === 'none' ? '' : 'TYPEA_');
        // The secondary score is only named when it is actually used for colour
        // allocation; an event that ignores it gets the shorter code.
        const scorePart = settings.useSecondaryScore === false ? primary : `${primary}_${secondary}`;
        return `FIDE_TEAM_${typePart}${scorePart}`;
    },

    /**
     * Every team record for one export, in the order the tournament section expects.
     *
     * Returned as two lists because they belong in different places in the document: the
     * tournament-section lines go with 012/022/…, while the team section and the
     * per-round records follow the player lines.
     *
     * @param {Object} arg
     * @param {Array}  arg.teams    [{ id, teamNo, name, nickname, strength, squad: [playerId] }]
     * @param {Array}  arg.rounds   rounds, each { roundNo, matches, pairings }
     * @param {Object} arg.settings { boards, matchPointSystem, primaryScore, colourPrefType,
     *                                pairingSystem, pointSystem }
     * @param {Function} arg.startNoOf  playerId -> starting rank number
     * @param {boolean} arg.includeNewRecords  emit 310 and the per-round records
     * @returns {{ tournamentLines: string[], teamLines: string[] }}
     */
    build({ teams, rounds, settings, startNoOf, includeNewRecords }) {
        teams = teams || [];
        rounds = rounds || [];
        settings = settings || {};
        const boards = parseInt(settings.boards, 10) || 4;

        const tournamentLines = [this.teamCountLine(teams)];
        const teamLines = [];

        if (includeNewRecords) {
            tournamentLines.push(this.colourSequenceLine(boards));
            const mps = this.matchPointSystemLine(settings.matchPointSystem);
            if (mps) tournamentLines.push(mps);
        }

        // Standings feed the match-point / game-point / rank columns of record 310, so the
        // file reports the same figures the arbiter published rather than a second opinion.
        const standings = (includeNewRecords && window.TeamEngine)
            ? window.TeamEngine.calculateTeamStandings(teams, rounds, settings)
            : [];
        const standingBy = new Map(standings.map(r => [r.teamId, r]));

        teams.slice()
            .sort((a, b) => (parseInt(a.teamNo, 10) || 0) - (parseInt(b.teamNo, 10) || 0))
            .forEach(t => {
                teamLines.push(this.teamLine(t, startNoOf));
                if (includeNewRecords) {
                    const s = standingBy.get(t.id);
                    teamLines.push(this.teamLine310({
                        ...t,
                        matchPoints: s ? s.matchPoints : 0,
                        gamePoints: s ? s.gamePoints : 0,
                        rank: s ? s.rank : ''
                    }, startNoOf));
                }
            });

        if (!includeNewRecords) return { tournamentLines, teamLines };

        // ---- per-round records ----------------------------------------------
        const teamById = new Map(teams.map(t => [t.id, t]));
        const noOf = (id) => parseInt((teamById.get(id) || {}).teamNo, 10) || 0;

        const byeByRound = [];
        const forfeitLines = [];
        const oodoLines = [];

        rounds.forEach(round => {
            let byeThisRound = null;
            (round.matches || []).forEach(m => {
                if (m.blackTeam === 'BYE') { byeThisRound = noOf(m.whiteTeam); return; }

                const boardGames = (round.pairings || []).filter(p => p.matchId === m.id);
                const lineupOf = (teamId) => {
                    const out = new Array(boards).fill(null);
                    boardGames.forEach(p => {
                        const idx = (p.board || 0) - 1;
                        if (idx < 0 || idx >= boards) return;
                        if (p.whiteTeam === teamId && p.white !== 'WO') out[idx] = p.white;
                        else if (p.blackTeam === teamId && p.black !== 'WO') out[idx] = p.black;
                    });
                    return out;
                };

                const wLineup = lineupOf(m.whiteTeam);
                const bLineup = lineupOf(m.blackTeam);
                const wEmpty = wLineup.every(id => !id);
                const bEmpty = bLineup.every(id => !id);

                // A whole team missing is a 330, and its line-up is then not reported at
                // all — there is no line-up to report.
                if (wEmpty || bEmpty) {
                    const type = wEmpty && bEmpty ? '--' : (bEmpty ? '+-' : '-+');
                    forfeitLines.push(this.forfeitLine(type, round.roundNo, noOf(m.whiteTeam), noOf(m.blackTeam)));
                    return;
                }

                [[m.whiteTeam, wLineup, m.blackTeam], [m.blackTeam, bLineup, m.whiteTeam]].forEach(([teamId, lineup, oppId]) => {
                    const team = teamById.get(teamId);
                    if (!team) return;
                    const line = this.outOfOrderLine(
                        round.roundNo, noOf(teamId), noOf(oppId),
                        lineup, team.squad || [], boards, startNoOf
                    );
                    if (line) oodoLines.push(line);
                });
            });
            byeByRound.push(byeThisRound);
        });

        // A PAB is worth a draw in both scorings (C.04.6 art. 2.5), so the record is
        // derived from the configured point systems rather than stored separately.
        const mDraw = Number((settings.matchPointSystem || '2-1-0').split('-')[1]);
        const pDraw = Number((settings.pointSystem || '1-0.5-0').split('-')[1]);
        const pab = this.pabLine(byeByRound, mDraw, pDraw * boards);
        if (pab) teamLines.push(pab);

        return {
            tournamentLines,
            teamLines: teamLines.concat(forfeitLines, oodoLines)
        };
    }
};

if (typeof module !== 'undefined') {
    module.exports = { TRFTeam };
} else {
    window.TRFTeam = TRFTeam;
}
